<template>
  <!-- Drop zone overlay -->
  <div
    class="fm-grid-root"
    @dragover.prevent="dragOver = true"
    @dragleave="dragOver = false"
    @drop.prevent="onRootDrop"
    :class="{ 'fm-grid--drag-over': dragOver }"
  >
    <!-- List header -->
    <div v-if="view === 'list'" class="fm-list-header">
      <span></span>
      <span class="fm-col-sortable" @click="toggleSort('name')">
        Name <i :class="sortIcon('name')"></i>
      </span>
      <span class="fm-col-sortable" @click="toggleSort('size')">
        Size <i :class="sortIcon('size')"></i>
      </span>
      <span class="fm-col-sortable" @click="toggleSort('modifiedAt')">
        Modified <i :class="sortIcon('modifiedAt')"></i>
      </span>
      <span>Type</span>
    </div>

    <!-- Empty state -->
    <div v-if="!sorted.length" class="fm-empty">
      <i class="bx bx-folder-open fm-empty-icon"></i>
      <p>{{ searchQuery ? 'No files match your search' : 'This folder is empty' }}</p>
    </div>

    <!-- File items -->
    <div
      v-else
      class="fm-items-container"
      :class="view === 'grid' ? 'fm-items-grid' : 'fm-items-list'"
      @click.self="$emit('clear-selection')"
    >
      <FileItem
        v-for="item in sorted"
        :key="item.id"
        :ref="el => itemRefs[item.id] = el"
        :item="item"
        :selected="selectedIds.has(item.id)"
        :view="view"
        :selectionMode="selectedIds.size > 0"
        @select="$emit('select', item)"
        @multi-select="$emit('multi-select', item)"
        @range-select="$emit('range-select', item)"
        @open="$emit('open', item)"
        @context-menu="$emit('context-menu', $event)"
        @rename="$emit('rename', $event)"
        @drop="$emit('move', $event)"
      />
    </div>

    <!-- Drag-over hint -->
    <div v-if="dragOver" class="fm-drag-hint">
      <i class="bx bx-upload"></i>
      <span>Drop to upload here</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import FileItem from './FileItem.vue';

const props = defineProps({
  items: { type: Array, default: () => [] },
  view: { type: String, default: 'grid' },
  selectedIds: { type: Object, default: () => new Set() }, // Set<id>
  searchQuery: { type: String, default: '' },
  sortBy: { type: String, default: 'name' },
  sortDir: { type: String, default: 'asc' },
});

const emit = defineEmits([
  'select', 'multi-select', 'range-select', 'open',
  'context-menu', 'rename', 'move', 'upload-files',
  'clear-selection', 'sort',
]);

const dragOver = ref(false);
const itemRefs = ref({});

const sorted = computed(() => {
  let list = [...props.items];

  if (props.searchQuery) {
    const q = props.searchQuery.toLowerCase();
    list = list.filter(i => i.name.toLowerCase().includes(q));
  }

  // Folders always first
  list.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    const dir = props.sortDir === 'asc' ? 1 : -1;
    if (props.sortBy === 'name') return dir * a.name.localeCompare(b.name);
    if (props.sortBy === 'size') return dir * ((a.size || 0) - (b.size || 0));
    if (props.sortBy === 'modifiedAt') {
      return dir * (new Date(a.modifiedAt || 0) - new Date(b.modifiedAt || 0));
    }
    return 0;
  });

  return list;
});

function toggleSort(col) {
  emit('sort', {
    by: col,
    dir: props.sortBy === col && props.sortDir === 'asc' ? 'desc' : 'asc',
  });
}

function sortIcon(col) {
  if (props.sortBy !== col) return 'bx bx-sort';
  return props.sortDir === 'asc' ? 'bx bx-sort-up' : 'bx bx-sort-down';
}

function onRootDrop(e) {
  dragOver.value = false;
  if (e.dataTransfer.files.length) {
    emit('upload-files', e.dataTransfer.files);
  }
}

// Expose for parent to trigger inline rename by id
function startRenameById(id) {
  itemRefs.value[id]?.startRename?.();
}
defineExpose({ startRenameById });
</script>

<style scoped>
.fm-grid-root {
  position: relative;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.fm-grid--drag-over { outline: 2px dashed var(--fm-accent); outline-offset: -4px; border-radius: 8px; }

.fm-list-header {
  display: grid;
  grid-template-columns: 32px 1fr 80px 110px 56px;
  padding: 4px 10px;
  gap: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--fm-text-muted);
  border-bottom: 1px solid var(--fm-border);
  position: sticky;
  top: 0;
  background: var(--fm-header-bg, var(--fm-surface));
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.fm-col-sortable {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}
.fm-col-sortable:hover { color: var(--fm-text); }

.fm-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: 2px;
  padding: 8px 6px;
}
.fm-items-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 4px;
}

.fm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--fm-text-muted);
}
.fm-empty-icon { font-size: 3.5rem; opacity: .4; }
.fm-empty p { font-size: 0.85rem; margin: 0; }

.fm-drag-hint {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--fm-item-selected);
  border-radius: 8px;
  pointer-events: none;
  font-size: 0.9rem;
  color: #a5b4fc;
}
.fm-drag-hint i { font-size: 2.5rem; }
</style>
